import { useMemo, useState } from "react";

import { api } from "../api/client";
import type { PasswordReset, RoleKey, User } from "../api/types";
import { failureMessage } from "../api/useResource";
import { usePagedResource } from "../api/usePagedResource";
import { DataTable } from "../components/data-table";
import type { DataTableRowAction } from "../components/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { ActionError, Badge, Button, Card, Confirm, Field, Modal, PageHeader, inputClass } from "../components/ui";
import { useSession } from "../store/session";
import { formatDateTime } from "./Calls";

// Usuarios del portal de esta cuenta.
//
// Una persona puede operar varias cuentas con el mismo correo: invitarla a una
// cuenta nueva no crea un usuario duplicado, le añade una membresía. Por eso
// «Quitar» la desvincula de esta cuenta y no borra a la persona: sigue
// entrando a las demás cuentas donde esté.

export function Users() {
  const users = usePagedResource<User>("/api/v1/users");
  const myUserId = useSession((s) => s.session?.user.id ?? "");

  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [removing, setRemoving] = useState<User | null>(null);
  const [resetting, setResetting] = useState<User | null>(null);
  // La temporal se muestra una vez y no se vuelve a ver: no se guarda en
  // claro, igual que el secreto de una llave de API.
  const [reset, setReset] = useState<PasswordReset | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function remove(user: User) {
    setActionError(null);
    setBusy(true);
    try {
      await api.delete(`/api/v1/users/${user.id}`);
      setRemoving(null);
      await users.reload(true);
    } catch (err) {
      setActionError(failureMessage(err, "no se pudo quitar al usuario"));
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword(user: User) {
    setActionError(null);
    setBusy(true);
    try {
      const data = await api.post<PasswordReset>(`/api/v1/users/${user.id}/password-reset`);
      setResetting(null);
      setReset(data);
      await users.reload(true);
    } catch (err) {
      setActionError(failureMessage(err, "no se pudo restablecer la contraseña"));
      setResetting(null);
    } finally {
      setBusy(false);
    }
  }

  const invited = (users.data ?? []).filter((u) => u.status === "invited");

  const columnas = useMemo<ColumnDef<User, any>[]>(
    () => [
      {
        id: "user",
        accessorFn: (user) => `${user.full_name} ${user.email}`,
        header: "Usuario",
        meta: { label: "Usuario" },
        cell: ({ row }) => {
          const user = row.original;
          return (
            <>
              <div className="font-medium text-slate-100">
                {user.full_name === "" ? user.email : user.full_name}
                {user.id === myUserId && (
                  <span className="ml-2 text-xs font-normal text-slate-500">(tú)</span>
                )}
              </div>
              {user.full_name !== "" && <div className="text-xs text-slate-500">{user.email}</div>}
            </>
          );
        },
      },
      {
        id: "role",
        accessorKey: "role",
        header: "Rol",
        meta: { label: "Rol" },
        cell: ({ row }) => (
          <span className="text-slate-300">{ROLE_LABELS[row.original.role] ?? row.original.role}</span>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Estado",
        meta: { label: "Estado" },
        cell: ({ row }) => (
          <Badge tone={USER_STATUS_TONES[row.original.status]}>
            {USER_STATUS_LABELS[row.original.status]}
          </Badge>
        ),
      },
      {
        id: "mfa_enabled",
        accessorFn: (user) => String(user.mfa_enabled),
        header: "Segundo factor",
        meta: { label: "Segundo factor" },
        cell: ({ row }) =>
          row.original.mfa_enabled ? (
            <Badge tone="good">Activo</Badge>
          ) : (
            <Badge tone="warn">Sin activar</Badge>
          ),
      },
      {
        id: "last_login_at",
        accessorKey: "last_login_at",
        header: "Último acceso",
        meta: { label: "Último acceso", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-slate-600">{formatDateTime(row.original.last_login_at)}</span>
        ),
      },
    ],
    [myUserId],
  );

  const acciones = useMemo<DataTableRowAction<User>[]>(
    () => [
      { label: "Editar", onSelect: (user) => setEditing(user) },
      {
        // La propia no se restablece desde aquí: este camino no pide probar
        // nada más que la sesión. La tuya se cambia en Seguridad.
        label: "Restablecer contraseña",
        hidden: (user) => user.id === myUserId,
        onSelect: (user) => setResetting(user),
      },
      {
        // Quitarse a uno mismo deja la cuenta sin administrador: el backend
        // lo rechaza, así que la acción no se ofrece.
        label: "Quitar de la cuenta",
        tone: "danger",
        hidden: (user) => user.id === myUserId,
        onSelect: (user) => setRemoving(user),
      },
    ],
    [myUserId],
  );

  return (
    <div>
      <PageHeader
        title="Usuarios"
        description="Quién puede entrar al portal de esta cuenta y con qué rol. El rol decide los permisos, y una llave de API nunca puede más que el rol de quien la creó."
        action={!inviting && <Button onClick={() => setInviting(true)}>Invitar usuario</Button>}
      />

      {actionError !== null && <ActionError message={actionError} />}

      {/*
        Sigue sin haber correo de invitación: lo que hay es «Restablecer
        contraseña», que entrega una temporal en pantalla para pasársela a la
        persona. Decir dónde está el botón es más útil que repetir que falta
        el correo, y no promete ningún envío que nadie hace.
      */}
      {invited.length > 0 && (
        <Card className="mb-6 border-amber-500/30 bg-amber-500/10 p-4">
          <p className="text-sm font-semibold text-amber-200">
            {invited.length === 1
              ? "Hay 1 usuario invitado que todavía no puede entrar"
              : `Hay ${invited.length} usuarios invitados que todavía no pueden entrar`}
          </p>
          <p className="mt-1 text-sm text-amber-200">
            No hay correo de invitación: la plataforma no manda correos. Dale «Restablecer
            contraseña» en su fila y pásale la temporal que aparece por un canal seguro; con ella
            entra y la cambia en Seguridad.
          </p>
        </Card>
      )}

      {inviting && (
        <InviteForm
          onCancel={() => setInviting(false)}
          onCreated={() => {
            setInviting(false);
            void users.reload(true);
          }}
        />
      )}

      {editing !== null && (
        <EditUserForm
          user={editing}
          isSelf={editing.id === myUserId}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void users.reload(true);
          }}
        />
      )}

      {removing !== null && (
        <Confirm
          title={`Quitar a ${removing.email} de esta cuenta`}
          description="Pierde el acceso al portal de esta cuenta de inmediato. La persona no se borra: si opera otras cuentas, sigue entrando a esas."
          confirmLabel="Quitar de la cuenta"
          busy={busy}
          onConfirm={() => void remove(removing)}
          onCancel={() => setRemoving(null)}
        />
      )}

      {resetting !== null && (
        <Confirm
          title={`Restablecer la contraseña de ${resetting.email}`}
          description="Su contraseña actual deja de servir y sus sesiones abiertas se cierran. Te damos una temporal que solo se muestra una vez: pásasela por un canal seguro para que entre y la cambie."
          confirmLabel="Restablecer"
          busy={busy}
          onConfirm={() => void resetPassword(resetting)}
          onCancel={() => setResetting(null)}
        />
      )}

      {reset !== null && (
        <Modal title="Contraseña restablecida" onClose={() => setReset(null)}>
          <p className="text-sm text-slate-300">
            Contraseña temporal de <strong className="text-slate-100">{reset.email}</strong>. No se
            vuelve a mostrar: si se pierde, se restablece otra vez.
          </p>
          <code className="mt-3 block break-all rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 font-mono text-sm text-sky-200">
            {reset.temporary_password}
          </code>
          <p className="mt-3 text-sm text-slate-400">
            Sus sesiones quedaron cerradas. Dile que la cambie en Seguridad al entrar: esta la
            conoces tú.
          </p>
          <div className="mt-4 flex justify-end">
            <Button onClick={() => setReset(null)}>Listo</Button>
          </div>
        </Modal>
      )}

      <DataTable<User>
        data={users.data}
        columns={columnas}
        getRowId={(user) => user.id}
        loading={users.loading}
        error={users.error}
        onRetry={() => void users.reload(false)}
        caption="Usuarios de la cuenta"
        empty={{
          title: "Sin usuarios",
          description: "Ni siquiera el tuyo: algo salió mal al cargar.",
        }}
        search={{ placeholder: "Buscar por nombre o correo…" }}
        filters={[
          {
            columnId: "role",
            label: "Rol",
            options: Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label })),
          },
          {
            columnId: "status",
            label: "Estado",
            options: [
              { value: "active", label: "Activo" },
              { value: "invited", label: "Invitado" },
              { value: "disabled", label: "Deshabilitado" },
            ],
          },
        ]}
        rowActions={acciones}
        hideableColumns
        urlStateKey="usuarios"
        pagination={{
          pageSize: users.enEstaPagina,
          hasNext: users.hayMas,
          hasPrevious: users.hayAnterior,
          onNext: users.siguiente,
          onPrevious: users.anterior,
        }}
      />
    </div>
  );
}

// Los roles que un administrador de cuenta puede asignar. `superadmin` no está:
// es de plataforma y no se reparte desde aquí (internal/auth/handlers_users.go,
// `assignableRoles`).
const ASSIGNABLE_ROLES: { role: RoleKey; label: string; description: string }[] = [
  {
    role: "tenant_admin",
    label: "Administrador de cuenta",
    description: "Todo lo de la cuenta: usuarios, llaves, números, saldo y límites.",
  },
  {
    role: "developer",
    label: "Desarrollador",
    description: "Origina llamadas, edita aplicaciones de voz y webhooks. No toca usuarios.",
  },
  {
    role: "billing_viewer",
    label: "Facturación",
    description: "Consulta saldo y consumo. No origina llamadas.",
  },
  { role: "read_only", label: "Solo lectura", description: "Consulta, sin modificar nada." },
];

const ROLE_LABELS: Record<string, string> = {
  superadmin: "Superadministrador",
  ...Object.fromEntries(ASSIGNABLE_ROLES.map((r) => [r.role, r.label])),
};

const USER_STATUS_LABELS: Record<User["status"], string> = {
  invited: "Invitado",
  active: "Activo",
  disabled: "Deshabilitado",
};

const USER_STATUS_TONES: Record<User["status"], "neutral" | "good" | "warn" | "bad"> = {
  invited: "warn",
  active: "good",
  disabled: "neutral",
};

function InviteForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<RoleKey>("read_only");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/v1/users", { email, full_name: fullName, role });
      onCreated();
    } catch (err) {
      setError(failureMessage(err, "no se pudo invitar al usuario"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Invitar a un usuario"
      description="Queda creado con su rol; todavía no podrá entrar."
      onClose={onCancel}
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Field
          label="Correo"
          hint="Si la persona ya tiene cuenta en la plataforma, se le añade esta cuenta en lugar de crear un usuario nuevo."
        >
          <input
            className={inputClass}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        <Field label="Nombre" hint="Opcional, para reconocerla en la lista.">
          <input className={inputClass} value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </Field>

        <fieldset>
          <legend className="text-sm font-medium text-slate-300">Rol</legend>
          <div className="mt-2 space-y-1">
            {ASSIGNABLE_ROLES.map((option) => (
              <label key={option.role} className="flex items-start gap-2 text-sm text-slate-300">
                <input
                  type="radio"
                  className="mt-1"
                  checked={role === option.role}
                  onChange={() => setRole(option.role)}
                />
                <span>
                  {option.label}
                  <span className="block text-xs text-slate-500">{option.description}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {error !== null && <p className="text-sm text-rose-300">{error}</p>}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Invitando…" : "Invitar"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EditUserForm({
  user,
  isSelf,
  onCancel,
  onSaved,
}: {
  user: User;
  isSelf: boolean;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [fullName, setFullName] = useState(user.full_name);
  const [role, setRole] = useState<RoleKey>(user.role);
  const [status, setStatus] = useState<"active" | "disabled">(
    user.status === "disabled" ? "disabled" : "active",
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // `superadmin` no es asignable desde aquí; si se está editando a un
  // superadmin, el selector de rol no se ofrece para no degradarlo por
  // accidente al guardar.
  const editableRole = user.role !== "superadmin";

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = { full_name: fullName, status };
      if (editableRole) body.role = role;
      await api.patch(`/api/v1/users/${user.id}`, body);
      onSaved();
    } catch (err) {
      setError(failureMessage(err, "no se pudo guardar"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={user.email}
      description="El correo no se puede cambiar: identifica la cuenta."
      onClose={onCancel}
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Field label="Nombre">
          <input className={inputClass} value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </Field>

        {editableRole ? (
          <fieldset>
            <legend className="text-sm font-medium text-slate-300">Rol</legend>
            <div className="mt-2 space-y-1">
              {ASSIGNABLE_ROLES.map((option) => (
                <label key={option.role} className="flex items-start gap-2 text-sm text-slate-300">
                  <input
                    type="radio"
                    className="mt-1"
                    checked={role === option.role}
                    onChange={() => setRole(option.role)}
                  />
                  <span>
                    {option.label}
                    <span className="block text-xs text-slate-500">{option.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        ) : (
          <p className="text-sm text-slate-400">
            Es superadministrador de la plataforma: el rol no se cambia desde el portal de una
            cuenta.
          </p>
        )}

        <Field
          label="Estado"
          hint={
            isSelf
              ? "Deshabilitarte a ti mismo te saca del portal en la siguiente petición."
              : "Deshabilitado no puede entrar, pero conserva su historial y sus membresías."
          }
        >
          <select
            className={inputClass}
            value={status}
            onChange={(e) => setStatus(e.target.value === "disabled" ? "disabled" : "active")}
          >
            <option value="active">Activo</option>
            <option value="disabled">Deshabilitado</option>
          </select>
        </Field>

        {error !== null && <p className="text-sm text-rose-300">{error}</p>}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
